import React, { useState, useEffect } from 'react';
import axios from './utils/axiosConfig';
import { useAuth } from './context/AuthContext';
import {
  Container,
  Table,
  Button,
  Modal,
  Form,
  Alert,
  Badge,
  Row,
  Col,
  Card,
  Tabs,
  Tab,
  ListGroup,
  Spinner
} from 'react-bootstrap';
import { FaPaperPlane, FaBullhorn, FaUsers, FaHistory, FaCheckCircle, FaTimesCircle, FaInbox, FaReply, FaEnvelopeOpen, FaRobot, FaCog, FaFlask, FaChartBar } from 'react-icons/fa';
import { MEMBER_TERM } from './config/clubTerms';

/**
 * LINE 訊息管理組件
 * 提供 LINE 訊息推播、群發、廣播功能
 */
function LineMessageManagement() {
  const { user } = useAuth();
  const token = localStorage.getItem('token');

  // 狀態管理
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // 用戶列表
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [filterSsid, setFilterSsid] = useState('');
  const [societies, setSocieties] = useState([]);

  // 訊息內容
  const [messageContent, setMessageContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');

  // 訊息歷史
  const [messageHistory, setMessageHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // 接收訊息
  const [receivedMessages, setReceivedMessages] = useState([]);
  const [receivedLoading, setReceivedLoading] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState(new Set()); // 展開的訊息 ID

  // 當前分頁
  const [activeTab, setActiveTab] = useState('push');

  // AI 助理設定
  const [aiSettings, setAiSettings] = useState({
    is_enabled: false,
    model: 'gpt-4o-mini',
    temperature: 0.7,
    max_tokens: 512,
    system_prompt: '',
    api_key_set: false,
  });
  const [aiApiKey, setAiApiKey] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiLogs, setAiLogs] = useState([]);
  const [aiStats, setAiStats] = useState({});
  const [aiTestMessage, setAiTestMessage] = useState('');
  const [aiTestResult, setAiTestResult] = useState(null);
  const [aiTestLoading, setAiTestLoading] = useState(false);

  // 載入社別列表
  const loadSocieties = async () => {
    try {
      const response = await axios.get('/line-messages/stores', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSocieties(response.data.data);
      }
    } catch (err) {
      console.error('載入社別列表失敗:', err);
    }
  };

  // 載入用戶列表
  const loadUsers = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterSsid) params.ss_id = filterSsid;

      const response = await axios.get('/line-messages/users', {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setUsers(response.data.data);
      }
    } catch (err) {
      setError('載入用戶列表失敗：' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // 載入訊息歷史
  const loadMessageHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await axios.get('/line-messages/history', {
        params: { limit: 50, offset: 0 },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setMessageHistory(response.data.data);
      }
    } catch (err) {
      console.error('載入訊息歷史失敗:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  // 載入接收訊息
  const loadReceivedMessages = async () => {
    try {
      setReceivedLoading(true);
      const response = await axios.get('/line-webhook/messages', {
        params: { limit: 100, offset: 0 },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setReceivedMessages(response.data.data);
      }
    } catch (err) {
      console.error('載入接收訊息失敗:', err);
    } finally {
      setReceivedLoading(false);
    }
  };

  // 標記訊息為已讀
  const markAsRead = async (messageId) => {
    try {
      await axios.put(`/line-webhook/messages/${messageId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadReceivedMessages();
    } catch (err) {
      console.error('標記已讀失敗:', err);
    }
  };

  // ==================== AI 助理功能 ====================
  const loadAiSettings = async () => {
    try {
      const response = await axios.get('/line-ai/settings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setAiSettings(response.data.data);
      }
    } catch (err) {
      console.error('載入 AI 設定失敗:', err);
    }
  };

  const saveAiSettings = async () => {
    try {
      setAiLoading(true);
      const payload = {
        is_enabled: aiSettings.is_enabled,
        model: aiSettings.model,
        temperature: aiSettings.temperature,
        max_tokens: aiSettings.max_tokens,
        system_prompt: aiSettings.system_prompt,
      };
      if (aiApiKey.trim()) {
        payload.api_key = aiApiKey.trim();
      }
      await axios.post('/line-ai/settings', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('AI 設定已儲存！');
      setAiApiKey('');
      loadAiSettings();
    } catch (err) {
      setError('儲存 AI 設定失敗：' + (err.response?.data?.error || err.message));
    } finally {
      setAiLoading(false);
    }
  };

  const loadAiLogs = async () => {
    try {
      const response = await axios.get('/line-ai/logs', {
        params: { limit: 50 },
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setAiLogs(response.data.data);
        setAiStats(response.data.stats);
      }
    } catch (err) {
      console.error('載入 AI 日誌失敗:', err);
    }
  };

  const handleAiTest = async () => {
    if (!aiTestMessage.trim()) return;
    try {
      setAiTestLoading(true);
      setAiTestResult(null);
      const response = await axios.post('/line-ai/test', { message: aiTestMessage }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiTestResult(response.data);
    } catch (err) {
      setAiTestResult({ success: false, error: err.response?.data?.error || err.message });
    } finally {
      setAiTestLoading(false);
    }
  };

  const resetAiPrompt = async () => {
    try {
      const response = await axios.get('/line-ai/default-prompt', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setAiSettings(prev => ({ ...prev, system_prompt: response.data.prompt }));
      }
    } catch (err) {
      console.error('重置提示詞失敗:', err);
    }
  };

  // 開啟回覆視窗
  const openReplyModal = (message) => {
    setSelectedMessage(message);
    setReplyText('');
    setShowReplyModal(true);
  };

  // 發送回覆
  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedMessage) return;

    try {
      setLoading(true);
      const response = await axios.post(
        `/line-webhook/messages/${selectedMessage.id}/reply`,
        { message: replyText },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccess('回覆發送成功！');
        setShowReplyModal(false);
        setReplyText('');
        loadReceivedMessages();
        // 自動展開該訊息以顯示回覆
        setExpandedMessages(prev => new Set([...prev, selectedMessage.id]));
      }
    } catch (err) {
      setError('回覆失敗：' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // 切換訊息展開/收合
  const toggleMessageExpand = (messageId) => {
    setExpandedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  // 初始化
  useEffect(() => {
    loadSocieties();
    loadUsers();
    loadMessageHistory();
    loadReceivedMessages();
  }, []);

  // 篩選條件改變時重新載入
  useEffect(() => {
    loadUsers();
  }, [filterSsid]);

  // 發送推播訊息
  const handlePushMessage = async () => {
    if (!messageContent.trim()) {
      setError('請輸入訊息內容');
      return;
    }

    if (selectedUsers.length === 0) {
      setError('請至少選擇一位接收者');
      return;
    }

    if (selectedUsers.length === 1) {
      // 單筆推播
      try {
        setLoading(true);
        setError(null);

        const response = await axios.post('/line-messages/push', {
          userId: selectedUsers[0],
          message: messageContent,
          imageUrl: imageUrl || undefined
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          setSuccess('訊息發送成功！');
          setMessageContent('');
          setImageUrl('');
          setSelectedUsers([]);
          loadMessageHistory();
        }
      } catch (err) {
        setError('訊息發送失敗：' + (err.response?.data?.error || err.message));
      } finally {
        setLoading(false);
      }
    } else {
      // 多筆群發
      handleMulticastMessage();
    }
  };

  // 發送群發訊息
  const handleMulticastMessage = async () => {
    if (!messageContent.trim()) {
      setError('請輸入訊息內容');
      return;
    }

    if (selectedUsers.length === 0) {
      setError('請至少選擇一位接收者');
      return;
    }

    if (selectedUsers.length > 500) {
      setError('群發對象超過上限 (最多 500 人)');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await axios.post('/line-messages/multicast', {
        userIds: selectedUsers,
        message: messageContent,
        imageUrl: imageUrl || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccess(`訊息已發送給 ${selectedUsers.length} 位用戶！`);
        setMessageContent('');
        setImageUrl('');
        setSelectedUsers([]);
        loadMessageHistory();
      }
    } catch (err) {
      setError('群發訊息失敗：' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // 發送廣播訊息
  const handleBroadcastMessage = async () => {
    if (!messageContent.trim()) {
      setError('請輸入訊息內容');
      return;
    }

    if (!window.confirm('確定要發送廣播訊息給所有 LINE 好友嗎？')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await axios.post('/line-messages/broadcast', {
        message: messageContent,
        imageUrl: imageUrl || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccess('廣播訊息發送成功！');
        setMessageContent('');
        setImageUrl('');
        loadMessageHistory();
      }
    } catch (err) {
      setError('廣播訊息失敗：' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // 處理用戶選擇
  const toggleUserSelection = (lineId) => {
    setSelectedUsers(prev =>
      prev.includes(lineId)
        ? prev.filter(id => id !== lineId)
        : [...prev, lineId]
    );
  };

  // 全選/取消全選
  const toggleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.e_line_id));
    }
  };

  // 渲染訊息類型標籤
  const renderMessageTypeBadge = (type) => {
    const types = {
      push: { text: '推播', variant: 'primary' },
      multicast: { text: '群發', variant: 'info' },
      broadcast: { text: '廣播', variant: 'warning' }
    };
    const config = types[type] || { text: type, variant: 'secondary' };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  // 渲染狀態標籤
  const renderStatusBadge = (status) => {
    return status === 'sent' ? (
      <Badge bg="success"><FaCheckCircle /> 成功</Badge>
    ) : (
      <Badge bg="danger"><FaTimesCircle /> 失敗</Badge>
    );
  };

  return (
    <Container fluid className="py-4">
      <h2 className="mb-4">
        <FaPaperPlane /> LINE 訊息管理
      </h2>

      {/* 錯誤訊息 */}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {/* 成功訊息 */}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
          {success}
        </Alert>
      )}

      {/* 分頁功能 */}
      <Tabs activeKey={activeTab} onSelect={(k) => { setActiveTab(k); if (k === 'ai') { loadAiSettings(); loadAiLogs(); } }} className="mb-3">
        {/* 推播/群發訊息 */}
        <Tab eventKey="push" title={<><FaPaperPlane /> 推播/群發訊息</>}>
          <Row>
            {/* 左側：用戶列表 */}
            <Col md={5}>
              <Card>
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0"><FaUsers /> 選擇接收者</h5>
                    <Badge bg="secondary">{selectedUsers.length} / {users.length}</Badge>
                  </div>
                </Card.Header>
                <Card.Body>
                  {/* 篩選條件 */}
                  <Form.Group className="mb-3">
                    <Form.Label>篩選社別</Form.Label>
                    <Form.Select
                      value={filterSsid}
                      onChange={(e) => setFilterSsid(e.target.value)}
                    >
                      <option value="">全部社別</option>
                      {societies.map(s => (
                        <option key={s.ss_id} value={s.ss_id}>
                          {s.ss_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  {/* 全選按鈕 */}
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="mb-3 w-100"
                    onClick={toggleSelectAll}
                  >
                    {selectedUsers.length === users.length ? '取消全選' : '全選'}
                  </Button>

                  {/* 用戶列表 */}
                  <ListGroup style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    {loading ? (
                      <div className="text-center py-3">
                        <Spinner animation="border" size="sm" />
                      </div>
                    ) : users.length === 0 ? (
                      <ListGroup.Item>無符合條件的用戶</ListGroup.Item>
                    ) : (
                      users.map(u => (
                        <ListGroup.Item
                          key={u.e_id}
                          active={selectedUsers.includes(u.e_line_id)}
                          onClick={() => toggleUserSelection(u.e_line_id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex justify-content-between">
                            <div>
                              <strong>{u.e_name}</strong>
                              <br />
                              <small className="text-muted">
                                {u.ss_name} | {u.e_mobile}
                              </small>
                            </div>
                            {selectedUsers.includes(u.e_line_id) && (
                              <FaCheckCircle className="text-success" />
                            )}
                          </div>
                        </ListGroup.Item>
                      ))
                    )}
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>

            {/* 右側：訊息編輯 */}
            <Col md={7}>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">編輯訊息內容</h5>
                </Card.Header>
                <Card.Body>
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label>訊息內容 *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={8}
                        value={messageContent}
                        onChange={(e) => setMessageContent(e.target.value)}
                        placeholder="請輸入要發送的訊息內容..."
                      />
                      <Form.Text className="text-muted">
                        已輸入 {messageContent.length} 字
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>圖片網址 (選填)</Form.Label>
                      <Form.Control
                        type="text"
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                        placeholder="https://example.com/image.jpg"
                      />
                      <Form.Text className="text-muted">
                        如需附上圖片，請輸入圖片的完整 URL
                      </Form.Text>
                    </Form.Group>

                    {/* 預覽區 */}
                    {imageUrl && (
                      <div className="mb-3">
                        <strong>圖片預覽：</strong>
                        <br />
                        <img
                          src={imageUrl}
                          alt="預覽"
                          style={{ maxWidth: '100%', maxHeight: '200px' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            setError('圖片載入失敗，請檢查網址');
                          }}
                        />
                      </div>
                    )}

                    {/* 發送按鈕 */}
                    <div className="d-grid gap-2">
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={handlePushMessage}
                        disabled={loading || !messageContent.trim() || selectedUsers.length === 0}
                      >
                        {loading ? (
                          <>
                            <Spinner animation="border" size="sm" /> 發送中...
                          </>
                        ) : (
                          <>
                            <FaPaperPlane /> 發送訊息給 {selectedUsers.length} 位用戶
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        {/* 廣播訊息 */}
        <Tab eventKey="broadcast" title={<><FaBullhorn /> 廣播訊息</>}>
          <Card>
            <Card.Header>
              <h5 className="mb-0"><FaBullhorn /> 廣播訊息給所有 LINE 好友</h5>
            </Card.Header>
            <Card.Body>
              <Alert variant="warning">
                <strong>⚠️ 注意：</strong>廣播訊息將發送給所有已加入 LINE 官方帳號的用戶，請謹慎使用！
              </Alert>

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>訊息內容 *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={8}
                    value={messageContent}
                    onChange={(e) => setMessageContent(e.target.value)}
                    placeholder="請輸入要廣播的訊息內容..."
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>圖片網址 (選填)</Form.Label>
                  <Form.Control
                    type="text"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                  />
                </Form.Group>

                <div className="d-grid">
                  <Button
                    variant="warning"
                    size="lg"
                    onClick={handleBroadcastMessage}
                    disabled={loading || !messageContent.trim()}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" /> 發送中...
                      </>
                    ) : (
                      <>
                        <FaBullhorn /> 發送廣播訊息
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Tab>

        {/* 接收訊息 */}
        <Tab eventKey="inbox" title={<><FaInbox /> 收件匣</>}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0"><FaInbox /> 接收到的訊息</h5>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={loadReceivedMessages}
                disabled={receivedLoading}
              >
                {receivedLoading ? <Spinner animation="border" size="sm" /> : '重新整理'}
              </Button>
            </Card.Header>
            <Card.Body>
              <ListGroup style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {receivedLoading ? (
                  <div className="text-center py-3">
                    <Spinner animation="border" />
                  </div>
                ) : receivedMessages.length === 0 ? (
                  <ListGroup.Item className="text-center text-muted">
                    暫無接收訊息
                  </ListGroup.Item>
                ) : (
                  receivedMessages.map((msg) => {
                    const isExpanded = expandedMessages.has(msg.id);
                    const hasReplies = msg.replies && msg.replies.length > 0;

                    return (
                      <ListGroup.Item
                        key={msg.id}
                        className={!msg.is_read ? 'border-primary' : ''}
                      >
                        {/* 訊息標題列 */}
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center">
                              <strong className="me-2">
                                {msg.user_name || '未綁定用戶'}
                              </strong>
                              {!msg.is_read && <Badge bg="primary">新</Badge>}
                              {hasReplies && (
                                <Badge bg="success" className="ms-2">
                                  {msg.replies.length} 則回覆
                                </Badge>
                              )}
                              <Badge
                                bg={
                                  msg.message_type === 'text' ? 'info' :
                                  msg.message_type === 'image' ? 'success' :
                                  msg.message_type === 'sticker' ? 'warning' : 'secondary'
                                }
                                className="ms-2"
                              >
                                {msg.message_type === 'text' ? '文字' :
                                 msg.message_type === 'image' ? '圖片' :
                                 msg.message_type === 'sticker' ? '貼圖' :
                                 msg.message_type === 'location' ? '位置' : msg.message_type}
                              </Badge>
                            </div>
                            <small className="text-muted">
                              {msg.user_society || '未知社別'} | {new Date(msg.received_at).toLocaleString('zh-TW')}
                            </small>
                          </div>
                          <div className="text-end">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-2"
                              onClick={() => openReplyModal(msg)}
                            >
                              <FaReply /> 回覆
                            </Button>
                            {!msg.is_read && (
                              <Button
                                variant="outline-success"
                                size="sm"
                                className="me-2"
                                onClick={() => markAsRead(msg.id)}
                              >
                                <FaEnvelopeOpen />
                              </Button>
                            )}
                            {hasReplies && (
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => toggleMessageExpand(msg.id)}
                              >
                                {isExpanded ? '收合' : '展開'}
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* 用戶訊息內容 */}
                        <div className="bg-light p-3 rounded mb-2">
                          <div className="d-flex">
                            <div className="me-2">👤</div>
                            <div className="flex-grow-1">
                              {msg.message_type === 'text' && (
                                <p className="mb-0">{msg.message_text}</p>
                              )}
                              {msg.message_type === 'location' && (
                                <p className="mb-0">📍 {msg.address}</p>
                              )}
                              {msg.message_type === 'sticker' && (
                                <p className="mb-0">🎨 貼圖 (Package: {msg.sticker_package_id}, ID: {msg.sticker_id})</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* 展開的回覆歷史 */}
                        {isExpanded && hasReplies && (
                          <div className="ps-4 border-start border-3 border-success">
                            {msg.replies.map((reply, idx) => (
                              <div key={reply.id} className="mb-2">
                                <div className="bg-success bg-opacity-10 p-3 rounded">
                                  <div className="d-flex">
                                    <div className="me-2">🤖</div>
                                    <div className="flex-grow-1">
                                      <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                          <strong className="text-success">
                                            {reply.is_auto_reply ? '自動回覆' : (reply.sender_name || '系統')}
                                          </strong>
                                          <br />
                                          <small className="text-muted">
                                            {new Date(reply.created_at).toLocaleString('zh-TW')}
                                          </small>
                                        </div>
                                        {reply.is_auto_reply && (
                                          <Badge bg="warning" text="dark">自動</Badge>
                                        )}
                                      </div>
                                      <p className="mb-0 mt-2">{reply.reply_message}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </ListGroup.Item>
                    );
                  })
                )}
              </ListGroup>
            </Card.Body>
          </Card>
        </Tab>

        {/* 訊息歷史 */}
        <Tab eventKey="history" title={<><FaHistory /> 訊息歷史</>}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0"><FaHistory /> 訊息發送歷史</h5>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={loadMessageHistory}
                disabled={historyLoading}
              >
                {historyLoading ? <Spinner animation="border" size="sm" /> : '重新整理'}
              </Button>
            </Card.Header>
            <Card.Body>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>發送時間</th>
                    <th>發送者</th>
                    <th>類型</th>
                    <th>接收者</th>
                    <th>訊息內容</th>
                    <th>狀態</th>
                  </tr>
                </thead>
                <tbody>
                  {historyLoading ? (
                    <tr>
                      <td colSpan="7" className="text-center">
                        <Spinner animation="border" />
                      </td>
                    </tr>
                  ) : messageHistory.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center">
                        暫無訊息歷史
                      </td>
                    </tr>
                  ) : (
                    messageHistory.map((msg, idx) => (
                      <tr key={msg.id}>
                        <td>{idx + 1}</td>
                        <td>{new Date(msg.created_at).toLocaleString('zh-TW')}</td>
                        <td>
                          {msg.sender_name}
                          <br />
                          <small className="text-muted">{msg.sender_position}</small>
                        </td>
                        <td>{renderMessageTypeBadge(msg.message_type)}</td>
                        <td>
                          {msg.message_type === 'broadcast' ? (
                            '全部用戶'
                          ) : msg.receiver_count ? (
                            `${msg.receiver_count} 位用戶`
                          ) : (
                            <small className="text-muted">{msg.receiver_line_id}</small>
                          )}
                        </td>
                        <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {msg.message_content}
                        </td>
                        <td>{renderStatusBadge(msg.status)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        {/* AI 助理設定 */}
        <Tab eventKey="ai" title={<><FaRobot /> AI 助理</>}>
          <Row>
            {/* 左側：設定 */}
            <Col lg={6}>
              <Card className="mb-3">
                <Card.Header><FaCog /> AI 助理設定</Card.Header>
                <Card.Body>
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        id="ai-enabled"
                        label={<strong>{aiSettings.is_enabled ? '✅ AI 已啟用' : '❌ AI 未啟用'}</strong>}
                        checked={aiSettings.is_enabled}
                        onChange={(e) => setAiSettings(prev => ({ ...prev, is_enabled: e.target.checked }))}
                      />
                      <Form.Text className="text-muted">
                        啟用後，當收到 LINE 訊息且無匹配的自動回覆規則時，將由 AI 回覆
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>OpenAI API Key</Form.Label>
                      <Form.Control
                        type="password"
                        value={aiApiKey}
                        onChange={(e) => setAiApiKey(e.target.value)}
                        placeholder={aiSettings.api_key_set ? '（已設定，留空則不更改）' : '請輸入 API Key'}
                      />
                      {aiSettings.api_key_set && (
                        <Form.Text className="text-success">✅ API Key 已設定</Form.Text>
                      )}
                    </Form.Group>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>模型</Form.Label>
                          <Form.Select
                            value={aiSettings.model}
                            onChange={(e) => setAiSettings(prev => ({ ...prev, model: e.target.value }))}
                          >
                            <option value="gpt-4o-mini">GPT-4o Mini（推薦，便宜快速）</option>
                            <option value="gpt-4o">GPT-4o（較聰明，較貴）</option>
                            <option value="gpt-4.1-mini">GPT-4.1 Mini</option>
                            <option value="gpt-4.1-nano">GPT-4.1 Nano（最便宜）</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>溫度 ({aiSettings.temperature})</Form.Label>
                          <Form.Range
                            min={0} max={1} step={0.1}
                            value={aiSettings.temperature}
                            onChange={(e) => setAiSettings(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>最大 Tokens</Form.Label>
                          <Form.Control
                            type="number"
                            value={aiSettings.max_tokens}
                            onChange={(e) => setAiSettings(prev => ({ ...prev, max_tokens: parseInt(e.target.value) }))}
                            min={100} max={2048}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>
                        系統提示詞 (System Prompt)
                        <Button variant="link" size="sm" onClick={resetAiPrompt}>重置為預設</Button>
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={10}
                        value={aiSettings.system_prompt}
                        onChange={(e) => setAiSettings(prev => ({ ...prev, system_prompt: e.target.value }))}
                        style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                      />
                      <Form.Text className="text-muted">
                        定義 AI 助理的行為、角色和回覆規則。AI 會根據此提示詞回覆{MEMBER_TERM}的訊息。
                      </Form.Text>
                    </Form.Group>

                    <Button variant="primary" onClick={saveAiSettings} disabled={aiLoading}>
                      {aiLoading ? <><Spinner animation="border" size="sm" /> 儲存中...</> : '💾 儲存設定'}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            {/* 右側：測試 + 統計 */}
            <Col lg={6}>
              {/* 測試區 */}
              <Card className="mb-3">
                <Card.Header><FaFlask /> AI 測試</Card.Header>
                <Card.Body>
                  <Form.Group className="mb-2">
                    <Form.Control
                      type="text"
                      value={aiTestMessage}
                      onChange={(e) => setAiTestMessage(e.target.value)}
                      placeholder="輸入測試訊息，例如：周慧芳的公司電話是多少？"
                      onKeyDown={(e) => e.key === 'Enter' && handleAiTest()}
                    />
                  </Form.Group>
                  <Button variant="info" size="sm" onClick={handleAiTest} disabled={aiTestLoading || !aiTestMessage.trim()}>
                    {aiTestLoading ? <><Spinner animation="border" size="sm" /> 處理中...</> : '🧪 測試回覆'}
                  </Button>

                  {aiTestResult && (
                    <Alert variant={aiTestResult.success ? 'success' : 'danger'} className="mt-3">
                      {aiTestResult.success ? (
                        <>
                          <strong>AI 回覆：</strong>
                          <div style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>{aiTestResult.reply}</div>
                          {aiTestResult.usage && (
                            <div className="mt-2 text-muted" style={{ fontSize: '0.85rem' }}>
                              Tokens: {aiTestResult.usage.total_tokens} | 耗時: {aiTestResult.usage.response_time_ms}ms
                            </div>
                          )}
                        </>
                      ) : (
                        <>{aiTestResult.error}</>
                      )}
                    </Alert>
                  )}
                </Card.Body>
              </Card>

              {/* 使用統計 */}
              <Card className="mb-3">
                <Card.Header><FaChartBar /> 近 30 天統計</Card.Header>
                <Card.Body>
                  <Row className="text-center">
                    <Col xs={3}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#0d6efd' }}>{aiStats.total_replies_30d || 0}</div>
                      <small className="text-muted">AI 回覆數</small>
                    </Col>
                    <Col xs={3}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#198754' }}>{aiStats.unique_users_30d || 0}</div>
                      <small className="text-muted">不同用戶</small>
                    </Col>
                    <Col xs={3}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#fd7e14' }}>{(aiStats.total_tokens_30d || 0).toLocaleString()}</div>
                      <small className="text-muted">總 Tokens</small>
                    </Col>
                    <Col xs={3}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#6f42c1' }}>{aiStats.avg_response_time_ms || 0}ms</div>
                      <small className="text-muted">平均回應</small>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* AI 回覆日誌 */}
              <Card>
                <Card.Header><FaHistory /> AI 回覆日誌</Card.Header>
                <Card.Body style={{ maxHeight: 400, overflowY: 'auto' }}>
                  {aiLogs.length === 0 ? (
                    <p className="text-muted text-center">尚無 AI 回覆記錄</p>
                  ) : (
                    <Table size="sm" hover>
                      <thead>
                        <tr>
                          <th>時間</th>
                          <th>{MEMBER_TERM}</th>
                          <th>訊息</th>
                          <th>Tokens</th>
                        </tr>
                      </thead>
                      <tbody>
                        {aiLogs.map(log => (
                          <tr key={log.id} title={`AI 回覆: ${log.ai_response}`}>
                            <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                              {new Date(log.created_at).toLocaleString('zh-TW', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td>{log.e_name || '未知'}</td>
                            <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {log.user_message}
                            </td>
                            <td><Badge bg="secondary">{log.total_tokens}</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>
      </Tabs>

      {/* 回覆訊息 Modal */}
      <Modal show={showReplyModal} onHide={() => setShowReplyModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title><FaReply /> 回覆訊息</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMessage && (
            <>
              <Card className="mb-3">
                <Card.Header>
                  <strong>{selectedMessage.user_name || '未綁定用戶'}</strong>
                  <br />
                  <small className="text-muted">
                    {new Date(selectedMessage.received_at).toLocaleString('zh-TW')}
                  </small>
                </Card.Header>
                <Card.Body>
                  {selectedMessage.message_type === 'text' && (
                    <p className="mb-0">{selectedMessage.message_text}</p>
                  )}
                  {selectedMessage.message_type === 'location' && (
                    <p className="mb-0">📍 {selectedMessage.address}</p>
                  )}
                </Card.Body>
              </Card>

              <Form.Group>
                <Form.Label>回覆內容</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="請輸入回覆訊息..."
                />
                <Form.Text className="text-muted">
                  {selectedMessage.is_replied
                    ? '此訊息已回覆過，系統將使用推播方式發送訊息'
                    : '首次回覆將使用 Reply Token（1分鐘內有效）'}
                </Form.Text>
              </Form.Group>

              {selectedMessage.replies && selectedMessage.replies.length > 0 && (
                <div className="mt-3">
                  <strong>歷史回覆:</strong>
                  <div className="mt-2" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                    {selectedMessage.replies.map((reply, idx) => (
                      <div key={idx} className="bg-light p-2 rounded mb-2">
                        <small className="text-muted">
                          {new Date(reply.created_at).toLocaleString('zh-TW')}
                          {reply.is_auto_reply && <Badge bg="warning" className="ms-2">自動</Badge>}
                        </small>
                        <p className="mb-0 mt-1">{reply.reply_message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReplyModal(false)}>
            取消
          </Button>
          <Button
            variant="primary"
            onClick={handleSendReply}
            disabled={loading || !replyText.trim()}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" /> 發送中...
              </>
            ) : (
              <>
                <FaPaperPlane /> 發送回覆
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default LineMessageManagement;
